import { useCallback, useEffect, useMemo, useState } from "react";
import { searchOrderInvoiceService as defaultService } from "../services/searchOrderInvoiceService";
import { useGetSearchOrderInvoice } from "./useGetSearchOrderInvoice";

// ─── Constants ────────────────────────────────────────────────────────────────

const PAGE_SIZE = 15;

// ─── Pure Logic ───────────────────────────────────────────────────────────────

export const buildOrderSearchParams = ({
  userId,
  companyInvoiceId,
  from,
  size,
  sort,
  search,
  serviceType,
}) => ({
  userId,
  companyInvoiceId,
  from,
  size,
  sort,
  search,
  serviceType,
});

export const deriveOrderPageState = orderPage => ({
  orders: orderPage?.orders ?? [],
  totalOrders: orderPage?.totalOrders ?? 0,
  totalPages: orderPage?.totalPages ?? 0,
});

// ─── Hook ─────────────────────────────────────────────────────────────────────

/**
 * Orchestrating hook for the order transactions table.
 *
 * Search and service type are both server-side, committed on `apply()`.
 * Resets to first page when companyInvoiceId changes or apply() is called.
 *
 * `service` is injected for testability.
 */
export function useSearchOrderInvoice(
  userId,
  companyInvoiceId,
  service = defaultService,
) {
  // ─── Pending filter state (user editing, not yet applied) ─────────────────
  const [pendingSearch, setPendingSearch] = useState("");
  const [pendingType, setPendingType] = useState("");

  // ─── Committed state (drives API params) ──────────────────────────────────
  const [committedSearch, setCommittedSearch] = useState("");
  const [committedType, setCommittedType] = useState("");

  // ─── Pagination + sort ───────────────────────────────────────────────────
  const [page, setPage] = useState(0);
  const [sort, setSort] = useState("DATE_ASC");

  useEffect(() => {
    setPage(0);
  }, [companyInvoiceId]);

  const params = useMemo(
    () =>
      buildOrderSearchParams({
        userId,
        companyInvoiceId,
        from: page * PAGE_SIZE,
        size: PAGE_SIZE,
        sort,
        search: committedSearch,
        serviceType: committedType,
      }),
    [userId, companyInvoiceId, page, sort, committedSearch, committedType],
  );

  const { status, orderPage, error, refresh } = useGetSearchOrderInvoice(
    params,
    service,
  );

  const derived = useMemo(() => deriveOrderPageState(orderPage), [orderPage]);

  // ─── Actions ─────────────────────────────────────────────────────────────

  const apply = useCallback(() => {
    setCommittedSearch(pendingSearch);
    setCommittedType(pendingType);
    setPage(0);
  }, [pendingSearch, pendingType]);

  const changePage = useCallback(nextPage => setPage(nextPage), []);

  return {
    status,
    error,
    orders: derived.orders,
    totalOrders: derived.totalOrders,
    totalPages: derived.totalPages,
    page,
    pageSize: PAGE_SIZE,
    sort,
    search: pendingSearch,
    typeFilter: pendingType,
    setSearch: setPendingSearch,
    setTypeFilter: setPendingType,
    setSort,
    setPage: changePage,
    apply,
    refresh,
  };
}
